import { TFunction } from 'i18next'

import { DEFAULT_CURRENCY } from '@features/invoices/constants/defaultCurrency'
import { DEFAULT_INVOICE_LANGUAGE } from '@features/invoices/constants/defaultInvoiceLanguage'
import { DEFAULT_INVOICE_PAYMENT_TERM } from '@features/invoices/constants/defaultInvoicePaymentTerm'
import { invoiceLanguageToLocale } from '@features/invoices/constants/invoiceLanguageToLocale'
import { DEFAULT_COLOR } from '@features/invoicingSettings/constants/defaultColor'
import { DEFAULT_FONT } from '@features/invoicingSettings/constants/defaultFont'
import { getAutomaticPaymentRemindersDefaultValues } from '@features/invoicingSettings/modules/paymentsAndReminders'
import { ProductType } from '@features/products/enums/productType'

import { InvoiceSettingsForm } from '../types/invoiceSettingsForm'
import { getLegalFormSettingsDefaultValues } from './getLegalFormSettingsDefaultValues'
import { getLegalMentionsDefaultValues } from './getLegalMentionsDefaultValues'

interface DefaultValuesOptions {
  predefinedValues?: Partial<InvoiceSettingsForm>
  organizationName?: string
}

export const getDefaultValues = (
  t: TFunction,
  options?: DefaultValuesOptions & { isFrenchComplianceMode?: boolean },
): InvoiceSettingsForm => {
  const { predefinedValues, organizationName, isFrenchComplianceMode } = options || {}
  return {
    automaticPaymentRemindersSettings:
      predefinedValues?.automaticPaymentRemindersSettings ||
      getAutomaticPaymentRemindersDefaultValues(t, organizationName),
    contactEmail: predefinedValues?.contactEmail || '',
    contactFirstName: predefinedValues?.contactFirstName || '',
    contactLastName: predefinedValues?.contactLastName || '',
    contactPhone: predefinedValues?.contactPhone || '',
    currency: predefinedValues?.currency || DEFAULT_CURRENCY,
    footerNote: predefinedValues?.footerNote || '',
    language: predefinedValues?.language || invoiceLanguageToLocale[DEFAULT_INVOICE_LANGUAGE],
    ...(isFrenchComplianceMode
      ? {
          legalFormSettings: {
            ...getLegalFormSettingsDefaultValues(),
            ...predefinedValues?.legalFormSettings,
          },
          legalMentions: {
            ...getLegalMentionsDefaultValues(),
            ...predefinedValues?.legalMentions,
            custom: {
              ...getLegalMentionsDefaultValues().custom,
              ...predefinedValues?.legalMentions?.custom,
            },
          },
        }
      : {}),
    logoId: predefinedValues?.logoId || null,
    logoUrl: predefinedValues?.logoUrl || undefined,
    message: predefinedValues?.message || '',
    paymentMethods: {
      bank: predefinedValues?.paymentMethods?.bank || '',
      iban: predefinedValues?.paymentMethods?.iban || '',
      offlinePayment: predefinedValues?.paymentMethods?.offlinePayment || '',
      paypalEmail: predefinedValues?.paymentMethods?.paypalEmail || '',
      swift: predefinedValues?.paymentMethods?.swift || '',
    },
    paymentTerm: predefinedValues?.paymentTerm ?? DEFAULT_INVOICE_PAYMENT_TERM,
    productType: predefinedValues?.productType || ProductType.Goods,
    shouldApplyTemplateColorToColumnHeaders: predefinedValues?.shouldApplyTemplateColorToColumnHeaders ?? true,
    shouldApplyTemplateColorToDividers: predefinedValues?.shouldApplyTemplateColorToDividers ?? true,
    shouldApplyTemplateColorToHeader: predefinedValues?.shouldApplyTemplateColorToHeader ?? true,
    shouldApplyTemplateColorToTotal: predefinedValues?.shouldApplyTemplateColorToTotal ?? true,
    shouldShowCustomerId: predefinedValues?.shouldShowCustomerId ?? false,
    shouldShowTermsAndConditions: predefinedValues?.shouldShowTermsAndConditions ?? false,
    templateColor: predefinedValues?.templateColor || DEFAULT_COLOR,
    templateFont: predefinedValues?.templateFont || DEFAULT_FONT,
    termsAndConditions: predefinedValues?.termsAndConditions || '',
    vatRateId: predefinedValues?.vatRateId,
    vatRatePercent: predefinedValues?.vatRatePercent,
  }
}
